import type { QueryClient } from "@tanstack/react-query";

import {
  invalidateDashboardAffectedQueries,
  isDashboardAffectedQueryKey,
} from "@/lib/query/dashboard-affected-queries";

/**
 * Request-path fragments for the resources a monitoring write can change beyond
 * the dashboard queries, which `dashboard-affected-queries` always adds.
 *
 * Signing off an out-of-range incident, acknowledging a check-failure alert and
 * acknowledging a hot-holding alert all resolve an alert episode; a synced log
 * entry moves the counts. Between them these touch four surfaces: the Monitoring tables and their Alerts badge, the
 * dashboard status that drives the summary cards, the priority list and Today's
 * status, and the incident's own detail view.
 *
 * The monitoring key embeds `scope`, `sortBy`, `limit` and `offset` from
 * component state, so it can't be reconstructed from a mutation site — one of the
 * reasons the shared predicate matches on a path fragment rather than the whole
 * key.
 *
 * The Dexie-backed open-alert rails are deliberately out of reach: they hold no
 * React Query entry, and `useOfflineOpenIncidents` /
 * `useOfflineOpenHotHoldingAlerts` already drop acknowledged rows optimistically
 * and re-read when the sync queue drains.
 */
const MONITORING_AFFECTED_KEY_PATHS = [
  "/monitoring",
  "/out-of-range-incidents",
  // The Logs tab's per-session "N entries" badge. It is served by
  // /log-sessions/today, not by /monitoring, so nothing above reaches it — a
  // practitioner who recorded a check kept seeing the previous count for the
  // full five-minute staleTime, on the one screen where they go to confirm
  // their own work landed.
  "/log-sessions",
  // The emergency lighting history. Every submit goes through the sync queue,
  // so a queued check that lands later would otherwise stay missing from it.
  "/emergency-light-checks",
] as const;

/**
 * True when a React Query key belongs to a resource that a monitoring write can
 * change. Exported for the drift-guard test.
 */
export function isMonitoringAffectedQueryKey(
  queryKey: readonly unknown[],
): boolean {
  return isDashboardAffectedQueryKey(queryKey, MONITORING_AFFECTED_KEY_PATHS);
}

/**
 * Invalidate every query that renders data a monitoring write can change.
 */
export async function invalidateMonitoringQueries(
  queryClient: QueryClient,
): Promise<void> {
  await invalidateDashboardAffectedQueries(queryClient, {
    keyPaths: MONITORING_AFFECTED_KEY_PATHS,
  });
}
